/**
 * Detect whether an MP4/MOV is "web-optimized" (a.k.a. faststart) — entirely client-side, from
 * the bytes already in the browser, so it needs no upload, no server round-trip and no ffmpeg.
 *
 * Why it matters: an ISO-BMFF file (`.mp4`/`.mov`) stores its index in the `moov` box. When
 * `moov` sits AFTER the media payload (`mdat`), a browser cannot begin playback until it has
 * downloaded far enough to reach `moov` — which for a large file means "the whole thing," i.e.
 * the "video takes forever to start" complaint. Re-muxing with `ffmpeg -movflags +faststart`
 * moves `moov` to the front so playback starts immediately and the rest streams via range
 * requests (which presigned S3 URLs already serve). No CDN required to get that win.
 *
 * How the check works: the top-level boxes are a flat sequence of `[size][type]` headers. We only
 * need to know which of `moov` / `mdat` appears FIRST — reading a small prefix of the file is
 * enough because the box right after the tiny `ftyp` decides it (we never read `mdat`'s payload,
 * we skip it by its declared size). `unknown` is returned for non-MP4 containers (e.g. WebM) and
 * anything we cannot parse, so callers can stay silent rather than warn on a false positive.
 */
export type FastStartResult = 'faststart' | 'not-faststart' | 'unknown';

/** MIME types / extensions this check understands (ISO base media file format). */
const ISO_BMFF_EXT = /\.(mp4|m4v|mov)$/i;

const isIsoBmff = (file: File): boolean =>
  /^video\/(mp4|quicktime|x-m4v)$/i.test(file.type) || ISO_BMFF_EXT.test(file.name);

/** How much of the head to read. Plenty: `ftyp` is tiny, so the deciding box header is early. */
const HEAD_BYTES = 256 * 1024;

const readUint32 = (view: DataView, offset: number): number => view.getUint32(offset);

const boxType = (bytes: Uint8Array, offset: number): string =>
  String.fromCharCode(bytes[offset], bytes[offset + 1], bytes[offset + 2], bytes[offset + 3]);

/**
 * Walk the top-level boxes in *buffer* and report whether `moov` precedes `mdat`.
 *
 * Returns `unknown` if the buffer ends before either box is seen (the head prefix was too small
 * for an unusual layout) rather than guessing. Exported for unit testing the parser directly.
 */
export function scanIsoBmffBoxes(buffer: ArrayBuffer): FastStartResult {
  const view = new DataView(buffer);
  const bytes = new Uint8Array(buffer);
  const length = bytes.byteLength;

  let offset = 0;

  // Each iteration reads one box header: 4-byte big-endian size, 4-byte type.
  while (offset + 8 <= length) {
    let size = readUint32(view, offset);
    const type = boxType(bytes, offset + 4);

    if (type === 'moov') return 'faststart';
    if (type === 'mdat') return 'not-faststart';

    let headerSize = 8;

    if (size === 1) {
      // 64-bit largesize follows the type. We only need the low 32 bits for skipping within the
      // small boxes that precede moov/mdat; a box that large before mdat does not occur in
      // practice, and if it did we would simply run out of buffer and return 'unknown'.
      if (offset + 16 > length) break;
      const high = readUint32(view, offset + 8);
      const low = readUint32(view, offset + 12);
      size = high * 2 ** 32 + low;
      headerSize = 16;
    } else if (size === 0) {
      // Box runs to end of file; nothing meaningful can follow, so we are done scanning.
      break;
    }

    if (size < headerSize) break; // malformed — stop rather than loop forever
    offset += size;
  }

  return 'unknown';
}

/**
 * Inspect *file* and report its faststart status. Only reads the first {@link HEAD_BYTES}.
 *
 * Never throws: any read/parse problem resolves to `unknown` so a detector failure can never
 * block or break an upload.
 */
export async function detectMp4FastStart(file: File): Promise<FastStartResult> {
  if (!isIsoBmff(file)) return 'unknown';

  try {
    const head = file.slice(0, Math.min(HEAD_BYTES, file.size));
    const buffer = await head.arrayBuffer();
    return scanIsoBmffBoxes(buffer);
  } catch {
    return 'unknown';
  }
}
